import { useState, useEffect } from 'react';
import api from '../services/api';

interface AppraisalCycle {
  id: string;
  code: string;
  cycle_name: string;
  cycle_start_date: string;
  cycle_end_date: string;
  financial_year: string;
  status: string;
}

export default function AppraisalCycles() {
  const [cycles, setCycles] = useState<AppraisalCycle[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    cycleName: '',
    cycleStartDate: '',
    cycleEndDate: '',
    financialYear: ''
  });

  useEffect(() => {
    fetchCycles();
  }, []);

  const fetchCycles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pas/cycles');
      setCycles(response.data.cycles || []);
    } catch (error) {
      console.error('Failed to fetch appraisal cycles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/pas/cycles', formData);
      setFormData({
        code: '',
        cycleName: '',
        cycleStartDate: '',
        cycleEndDate: '',
        financialYear: ''
      });
      setShowForm(false);
      fetchCycles();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create appraisal cycle');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Appraisal Cycles</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : 'Create Cycle'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Cycle Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="px-4 py-2 border rounded-lg"
                required
              />
              <input
                type="text"
                placeholder="Cycle Name"
                value={formData.cycleName}
                onChange={(e) => setFormData({ ...formData, cycleName: e.target.value })}
                className="px-4 py-2 border rounded-lg"
                required
              />
              <input
                type="date"
                value={formData.cycleStartDate}
                onChange={(e) => setFormData({ ...formData, cycleStartDate: e.target.value })}
                className="px-4 py-2 border rounded-lg"
              />
              <input
                type="date"
                value={formData.cycleEndDate}
                onChange={(e) => setFormData({ ...formData, cycleEndDate: e.target.value })}
                className="px-4 py-2 border rounded-lg"
              />
              <input
                type="text"
                placeholder="Financial Year (e.g., 2024-2025)"
                value={formData.financialYear}
                onChange={(e) => setFormData({ ...formData, financialYear: e.target.value })}
                className="px-4 py-2 border rounded-lg col-span-2"
              />
            </div>
            <button
              type="submit"
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Create Cycle
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-center text-gray-600">Loading appraisal cycles...</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">Code</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Cycle Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Financial Year</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Start Date</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">End Date</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {cycles.map((cycle) => (
                <tr key={cycle.id} className="border-b hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-semibold">{cycle.code}</td>
                  <td className="px-6 py-3 text-sm">{cycle.cycle_name}</td>
                  <td className="px-6 py-3 text-sm">{cycle.financial_year}</td>
                  <td className="px-6 py-3 text-sm">{cycle.cycle_start_date}</td>
                  <td className="px-6 py-3 text-sm">{cycle.cycle_end_date}</td>
                  <td className="px-6 py-3 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                      {cycle.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
